// External
import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Internal
import { ConfigService } from '../../../common/config/config.service';
import { UsercentricsUI, ValuesService } from '../../../common/values/values.service';
import { LinksService } from '../../links/links.service';
import { MessageService } from '../../services/core/message.service';
import { OffersService } from '../../services/process/offers/offers.service';
import { LanguageService } from '../../services/core/language.service';
import { ProfilesService } from '../../services/process/profiles/profiles.service';
import { PrivacyService } from '../../services/process/privacy/privacy.service';
import { PrivacyEvents } from '../../values/privacy.values.service';
import { AppsConfigService } from '../../config/apps.config.service';
import { ProductsConfigService } from '../../config/products.config.service';
import { ModalName, ModalRoutelessService } from '../ui/ui-modal-routeless/modal.routeless.service';
import { AdobeDataLayerService } from '../../services/core/adobe.datalayer.service';
import { ModalSize } from '../../services/global/modal/Modal.model';
import { SubscriptionsService } from '../../services/process/subscriptions/subscriptions.service';
import { PasswordManagerService } from '../../services/process/password-manager/password-manager.service';
import { IdTheftProtectionService } from '../../services/process/idtheftprotection/idtheftprotection.service';
import { ATOService } from '../../services/process/ato/ato.service';
import { HandleUnrecoverableStateService } from '../../services/global/handle-unrecoverable-state/handle-unrecoverable-state.service';
import { ParentalService } from '../../services/process/parental/parental.service';
import { ParentalNccService } from '../../services/process/parental-ncc/parental-ncc.service';
import { IDevicesService } from '../../services/interfaces/idevices/idevices.service';

interface DynamicMenuItem {
    timestampId: string,
    icon?: {
        type: string,
        icon: string
    },
    productName?: Function,
    textKey?: Function,
    path?: string,
    show: Function,
    available?: Function,
    requiresAttention?: Function,
    isNew?: Function,
    isExternal?: boolean,
    action?: Function
};

interface StaticMenuItem {
    timestampId: string,
    icon?: {
        type: string,
        icon: string
    },
    productName?: string,
    textKey?: string,
    path?: string,
    show: boolean,
    available: boolean,
    requiresAttention: boolean,
    isNew: boolean,
    isExternal?: boolean,
    action: Function
};

interface FooterLanguageInfos {
    flag: string,
    label: string,
    alt: string
};

interface MeniuFooter {
    terms: boolean,
    showChangeLanguage: boolean,
    lang: FooterLanguageInfos,
    copyright: {
        year: number,
        label: string
    }
};

@Component({
    selector: 'app-side-menu',
    templateUrl: './side-menu.component.html',
    styleUrls: ['./side-menu.component.scss']
})

export class SideMenuComponent implements OnInit {

    @Output() statusDrawer: EventEmitter<string> = new EventEmitter();
    private readonly onDestroy$: Subject<void> = new Subject<void>();

    // * textKey is translatable, while productName is inserted as it is
    sideMenu = {
        internal: [
            {
                // Dashboard
                timestampId: '1580325556',
                icon: {
                    type: 'menu',
                    icon: 'dashboard'
                },
                textKey: () => this.profilesService.isOwnerVSBAdmin() || this.profilesService.isFamilyPlanAdmin()
                        ? 'dashboard.main.title.vsb'
                        : 'dashboard.main.title',
                path: this.valuesService.centralPaths.dashboard.path,
                show: () => this.configService.getDashboard()
            },
            {
                // Family Dashboard
                timestampId: '1725958326',
                icon: {
                    type: 'web',
                    icon: 'social-network'
                },
                textKey: () => 'family.activity.dashboard.title',
                path: this.valuesService.centralPaths.family.path,
                show: () => this.configService.getFamilyDashboard()
            },
            {
                // Business Dashboard
                timestampId: '1708507450',
                icon: {
                    type: 'menu',
                    icon: 'apps'
                },
                textKey: () => 'business.dashboard.main.title',
                path: this.valuesService.centralPaths.business.path,
                show: () => this.configService.getBusinessDashboard()
            },
            {
                // Devices
                timestampId: '1580325650',
                icon: {
                    type: 'menu',
                    icon: 'devices'
                },
                textKey: () => this.iDevicesService.computeDevicesTitle(),
                path: this.valuesService.centralPaths.devices.path,
                show: () => this.configService.getDevices() && !this.subscriptionsService.hasATOConfiguration()
            }
        ],
        services: [
            {
                /* Security */
                timestampId: '1648643456',
                icon: {
                    type: 'app',
                    icon: 'protection-squircle-outline'
                },
                productName: () => this.productsConfigService.getProductName(this.valuesService.productNameSecurity),
                path: this.valuesService.centralPaths.security.path,
                show: () => this.configService.getSecurityModule(),
                isNew: () => !this.subscriptionsService.hasSecurityConfigured()
                            && !this.subscriptionsService.hasServicesOlderThan7Days(this.valuesService.appSecurity)
            },
            {
                /* ATO */
                timestampId: '1708352481',
                icon: {
                    type: 'products',
                    icon: 'ato'
                },
                productName: () => this.productsConfigService.getProductName(this.valuesService.productNameATO),
                path: this.valuesService.centralPaths.ato.path,
                show: () => this.configService.getAtoModule(),
                requiresAttention: () => !this.atoService.getMainReqHasError() && this.atoService.getSubsIsExpired(),
                isNew: () => !this.atoService.getIdentityWasCreated() && !this.subscriptionsService.hasServicesOlderThan7Days(this.valuesService.appATO)
            },
            {
                // Id Theft Protection
                timestampId: '1648643208',
                icon: {
                    type: 'products',
                    icon: 'idTheft-free'
                },
                productName: () => this.productsConfigService.getProductName(this.valuesService.productNameIDTP),
                path: this.valuesService.centralPaths.idtheftprotection.path,
                available: () => this.subscriptionsService.hasIdTheftProtection(),
                show: () => this.appsConfigService.showApp(this.valuesService.appIdTheft),
                requiresAttention: () => this.idTheftEnrollmentResolved
                                        && this.subscriptionsService.hasIdTheftProtection()
                                        && !this.idTheftProtectionService.getOnboardingDone(),
                isNew: () => this.idTheftEnrollmentResolved
                            && this.subscriptionsService.hasIdTheftProtection()
                            && !this.idTheftProtectionService.getOnboardingDone()
                            && !this.subscriptionsService.hasServicesOlderThan7Days(this.valuesService.appIdTheft)
            },
            {
                // Password Manager
                timestampId: '1637061417',
                icon: {
                    type: 'products',
                    icon: 'passwordManager-free'
                },
                productName: () => this.productsConfigService.getProductName(this.valuesService.productNamePasswordManager),
                path: this.valuesService.centralPaths.passwordmanager.path,
                available: () => this.subscriptionsService.hasPasswordManager(),
                show: () => this.appsConfigService.showApp(this.valuesService.appPassManager),
                requiresAttention: () => this.saferpassStatusResolved
                                        && this.subscriptionsService.hasPasswordManager()
                                        && !this.passwordManagerService.getSaferpassUserRegistered(),
                isNew: () => this.saferpassStatusResolved
                            && this.subscriptionsService.hasPasswordManager()
                            && !this.passwordManagerService.getSaferpassUserRegistered()
                            && !this.subscriptionsService.hasServicesOlderThan7Days(this.valuesService.appPassManager)
            },
            {
                // Secure Pass
                timestampId: '1731414712',
                icon: {
                    type: 'products',
                    icon: 'securepass'
                },
                productName: () => this.productsConfigService.getProductName(this.valuesService.productNameSecurePass),
                path: this.valuesService.centralPaths.securepass.path,
                available: () => this.subscriptionsService.hasSecurePass(),
                show: () => this.appsConfigService.showApp(this.valuesService.appSecurePass),
                requiresAttention: () => this.lokiMasterPasswordStatusResolved
                                        && this.subscriptionsService.hasSecurePass()
                                        && !this.passwordManagerService.getSecurePassUserRegistered(),
                isNew: () => this.lokiMasterPasswordStatusResolved
                            && this.subscriptionsService.hasSecurePass()
                            && !this.passwordManagerService.getSecurePassUserRegistered()
                            && !this.subscriptionsService.hasServicesOlderThan7Days(this.valuesService.appSecurePass)
            },
            {
                // Privacy
                timestampId: '1580325751',
                icon: {
                    type: 'products',
                    icon: 'dip-free'
                },
                productName: () => this.productsConfigService.getProductName(this.valuesService.productNameDIP),
                path: this.valuesService.centralPaths.privacy.path,
                available: () => this.subscriptionsService.hasDataPrivacy(),
                show: () => this.appsConfigService.showApp(this.valuesService.appDIP),
                requiresAttention: () => !this.privacyService.getMainReqHasError() &&
                                        (this.dipHasParkedIdentity() ||
                                            (this.subscriptionsService.hasDataPrivacy() &&
                                            !this.privacyService.onboardingIsDone())),
                isNew: () => !this.privacyService.getMainReqHasError() &&
                            (this.subscriptionsService.hasDataPrivacy()
                                && !this.privacyService.onboardingIsDone()
                                && !this.subscriptionsService.hasServicesOlderThan7Days(this.valuesService.appDIP))
            },
            {
                // VPN
                timestampId: '1636707113',
                icon: {
                    type: 'products',
                    icon: 'vpn-standard'
                },
                productName: () => this.productsConfigService.getProductName(this.valuesService.productNameVPNBasic),
                path: this.valuesService.centralPaths.vpn.path,
                available: () => this.subscriptionsService.hasVpn(),
                show: () => this.appsConfigService.showApp(this.valuesService.appVPN),
                isNew: () => this.subscriptionsService.hasVpn()
                            && !this.subscriptionsService.hasVpnConfigured()
                            && !this.subscriptionsService.hasServicesOlderThan7Days(this.valuesService.appVPN)
            },
            {
                // Parental
                timestampId: '1580325699',
                icon: {
                    type: 'products',
                    icon: 'parental-free'
                },
                productName: () => this.productsConfigService.getProductName(this.valuesService.productNamePA),
                path: this.valuesService.centralPaths.parental.path,
                available: () => this.subscriptionsService.hasParental(),
                show: () => this.appsConfigService.showApp(this.valuesService.appPA),
                requiresAttention: () => this.subscriptionsService.hasParental()
                                        && this.parentalService.showParentalUpgradeAlert(),
                isNew: () => this.subscriptionsService.hasParental()
                            && !this.profilesService.hasParentalProfiles()
                            && !this.subscriptionsService.hasServicesOlderThan7Days(this.valuesService.appPA)
            },
            {
                // Parental NCC
                timestampId: '1580325700',
                icon: {
                    type: 'products',
                    icon: 'ncc-free'
                },
                productName: () => this.productsConfigService.getProductName(this.valuesService.productNamePANCC),
                path: this.valuesService.centralPaths.parentalncc.path,
                available: () => this.subscriptionsService.hasParentalNCC(),
                show: () => this.appsConfigService.showApp(this.valuesService.appPANCC),
                requiresAttention: () => this.subscriptionsService.hasParentalNCC()
                                        && this.parentalNccService.showOnboardingNccModal(),
                isNew: () => this.subscriptionsService.hasParentalNCC()
                            && !this.profilesService.hasParentalProfiles()
                            && !this.subscriptionsService.hasServicesOlderThan7Days(this.valuesService.appPANCC)
            },
            {
                // Premium Services
                timestampId: '1580325788',
                icon: {
                    type: 'feedback',
                    icon: 'bookmark'
                },
                textKey: () => 'techassist.main.title',
                path: this.valuesService.centralPaths.premium.path,
                available: () => this.subscriptionsService.hasPremiumServices(),
                show: () => this.configService.getPremiumServices()
            }
        ],
        other: [
            {
                // Subscriptions
                timestampId: '1580325827',
                icon: {
                    type: 'feature',
                    icon: 'subscriptions'
                },
                textKey: () => 'main.subscriptions',
                path: this.profilesService.getSubscriptionsPath(this.valuesService.centralPaths.subscriptions.services.id),
                show: () => this.configService.getSubscriptions(),
                requiresAttention: () => this.subscriptionsService.subscriptionsPageRequiresAttention()
            },
            {
                // Offers
                timestampId: '1580325876',
                icon: {
                    type: 'feature',
                    icon: 'offers-v2'
                },
                textKey: () => 'offers.main.title',
                path: this.valuesService.centralPaths.offers.path,
                show: () => this.configService.getOffers(),
                requiresAttention: () => this.offersService.getNumber()
                                         && this.offersService.getShowOffers() !== this.valuesService.offersSeenValue
                                         && !this.offersService.showOffersCampaign(),
                campaign: {
                    show: this.offersService.showOffersCampaign(),
                    icon: this.valuesService.offersCampaign.menuIcon
                }
            },
            {
                // Referral
                timestampId: '1622652607',
                icon: {
                    type: 'menu',
                    icon: 'referral_alt'
                },
                productName: () => 'Invite and win',
                path: this.valuesService.centralPaths.referral.path,
                show: () => this.configService.getReferral()
            },
            {
                // Support internal
                timestampId: '1580325442',
                icon: {
                    type: 'menu',
                    icon: 'support'
                },
                textKey: () => 'main.support',
                path: this.valuesService.centralPaths.support.path,
                show: () => false
            },
            {
                // Support external
                timestampId: '1580325442',
                icon: {
                    type: 'menu',
                    icon: 'support'
                },
                textKey: () => 'main.support',
                path: this.linksService.support(),
                show: () => false,
                action: (event) => this.openExternalSupport(event),
                isExternal: true
            },
            {
                // Comunity
                timestampId: '1589101995',
                icon: {
                    type: 'web',
                    icon: 'society'
                },
                textKey: () => 'community.main.title',
                path: this.linksService.community(),
                available: () => false,
                show: () => false,
                action: event => this.openCommunity(event),
                isExternal: true
            },
            {
                // Info
                timestampId: '1641550652',
                textKey: () => 'main.info',
                show: () => this.configService.getInfo(),
                action: event => this.openInfoModal(event),
                isNew: () => false
            },
            {
                // Support
                timestampId: '1580325442',
                icon: {
                    type: 'menu',
                    icon: 'support'
                },
                productName: () => 'ヘルプ',
                path: this.linksService.support(),
                show: () => this.configService.getExternalSupport(),
                action: (event) => this.openExternalSupport(event),
                isExternal: true,
                isNew: () => false
            },
            {
                // Support 2
                timestampId: '1580325442',
                icon: {
                    type: 'menu',
                    icon: 'support'
                },
                productName: () => 'マイページを開く',
                show: () => this.configService.getExternalSupport(),
                action: (event) => this.openExternalSupportExtra(event, "https://www.sourcenext.com/security/rd/bd/107.html"),
                isExternal: true,
                isNew: () => false
            },
            {
                // Support 3
                timestampId: '1580325442',
                icon: {
                    type: 'menu',
                    icon: 'support'
                },
                productName: () => '追加購入',
                show: () => this.configService.getExternalSupport(),
                action: (event) => this.openExternalSupportExtra(event, "https://www.sourcenext.com/security/rd/bd/106.html"),
                isExternal: true,
                isNew: () => false
            },
        ]
    };

    public footer: MeniuFooter;

    public servicesSection: Array<StaticMenuItem> = [];
    public internalSection: Array<StaticMenuItem> = [];
    public otherSection: Array<StaticMenuItem> = [];

    private saferpassStatusResolved;
    private lokiMasterPasswordStatusResolved;
    private idTheftEnrollmentResolved;
    private readonly dynamicSubjectEventActions = {
        [this.valuesService.events.ownerChange]: () => this.setLanguage(),
        [this.valuesService.events.saferpassStatusChanged]: (payload) => {
            if (payload) {
                this.saferpassStatusResolved = true;
                this.initVars();
            }
        },
        [this.valuesService.events.lokiMasterPasswordStatusChanged]: (payload) => {
            if (payload) {
                this.lokiMasterPasswordStatusResolved = true;
                this.initVars();
            }
        },
        [this.valuesService.events.idTheftEnrollmentChanged]: (payload) => {
            if (payload) {
                this.idTheftEnrollmentResolved = true;
                this.initVars();
            }
        }
    };

    constructor(
        private readonly modalRoutelessService: ModalRoutelessService,
        private readonly router: Router,
        private readonly offersService: OffersService,
        private readonly messageService: MessageService,
        private readonly languageService: LanguageService,
        private readonly profilesService: ProfilesService,
        private readonly privacyService: PrivacyService,
        private readonly linksService: LinksService,
        private readonly appsConfigService: AppsConfigService,
        private readonly productsConfigService: ProductsConfigService,
        private readonly adobeDataLayerService: AdobeDataLayerService,
        private readonly parentalService: ParentalService,
        private readonly subscriptionsService: SubscriptionsService,
        private readonly passwordManagerService: PasswordManagerService,
        private readonly idTheftProtectionService: IdTheftProtectionService,
        private readonly atoService: ATOService,
        private readonly handleUnrecoverableStateService: HandleUnrecoverableStateService,
        private readonly parentalNccService: ParentalNccService,
        public readonly valuesService: ValuesService,
        public readonly configService: ConfigService,
        private readonly iDevicesService: IDevicesService
    ) {}

    //*
    //*  Life-Cycle Hooks
    //*
    //#region

    ngOnInit() {
        this.messageService.getMessage()
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(message => {
            switch (message?.text) {
                case this.valuesService.events.languageChange:
                case this.valuesService.events.dataResolverDone:
                    this.initVars();
                    this.languageService.updateCookiesSettingsModalLanguage();
                    break;
                case this.valuesService.events.offersSeen:
                    this.otherSection = this.computeStaticMenuItems(this.sideMenu.other);
                    break;
                case PrivacyEvents.PARKED_IDENTITY:
                    this.servicesSection = this.computeServices(this.sideMenu.services);
                    break;
                default:
                    break;
            }
        });

        for (const event in this.dynamicSubjectEventActions) {
            this.messageService.getDynamicSubject(event)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(payload => {
                this.dynamicSubjectEventActions[event](payload);
            });
        }

        this.initVars();
        this.languageService.updateCookiesSettingsModalLanguage();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
    //#endregion

    //*
    //* Helper functions
    //*
    //#region

    /**
     * This functions initiates component's variables.
     */
    initVars() {

        this.footer = {
            terms: this.configService.config.terms,
            showChangeLanguage: this.languageService.getShowChangeLanguageModal(),
            lang: this.computeFooterLanguageInfo(),
            copyright: {
                year: new Date().getFullYear(),
                label: this.productsConfigService.getManufacturerName()
            }
        };

        this.servicesSection = this.computeServices(this.sideMenu.services);
        this.internalSection = this.computeStaticMenuItems(this.sideMenu.internal);
        this.otherSection = this.computeStaticMenuItems(this.sideMenu.other);
    }

    /**
    * Computes menu items for the services section
    * @param {none}
    * @returns {Array<StaticMenuItem>} the sorted available services to be shown
    */
    private computeServices(sectionItems: Array<DynamicMenuItem>): Array<StaticMenuItem> {
        const items: Array<StaticMenuItem> = [];

        for (const item of sectionItems) {
            if (item.show()) {
                const menuItem = this.computedMenuItem(item);
                items.push(menuItem);
            }
        }
        items.sort((a, b) => a.available === b.available ? 0 : (a.available ? -1 : 1));
        return items;
    }

    /**
    * Computes static menu items to be shown based on the 'show' prop of the item
    * @param {none}
    * @returns {Array<StaticMenuItem>}
    */
    private computeStaticMenuItems(sectionItems: Array<DynamicMenuItem>): Array<StaticMenuItem> {
        const items: Array<StaticMenuItem> = [];

        for (const item of sectionItems) {
            if (item.show()) {
                const menuItem = this.computedMenuItem(item);
                items.push(menuItem);
            }
        }

        return items;
    }

    /**
    * Computes static menu item for a given menu entry, converts from dynamic get props to static ones
    * @private
    * @memberof SideMenuComponent
    * @param {DynamicMenuItem} item The dynamic menu item
    * @returns {StaticMenuItem} The computed menu item with static properties
    */
    private computedMenuItem(item: DynamicMenuItem): StaticMenuItem {
        return {
            timestampId: item.timestampId,
            icon: {
                type: item.icon?.type,
                icon: item.icon?.icon
            },
            productName: item.productName ? item.productName() : null,
            textKey: item?.textKey ? item.textKey() : null,
            path: item.path,
            show: item.show(),
            available: item.available ? item.available() : true,
            requiresAttention: item.requiresAttention ? item.requiresAttention() : false,
            isNew: item.isNew ? item.isNew() : false,
            isExternal: item.isExternal,
            action: item.action ?? (() => this.closeDrawers())
        };
    }

    /**
    * This sets new values for language related variables
    * @param {none}
    * @returns {nothing} Sets variables
    */
    setLanguage() {
        if (!this.footer?.lang) {
            return;
        }
        this.footer.lang = this.computeFooterLanguageInfo();
    }

    /**
    * This computes meniu footer values for language related variables
    * @param {none}
    * @returns {FooterLanguageInfos} footer language info by language
    */
    private computeFooterLanguageInfo(): FooterLanguageInfos {
        return  {
            flag: this.languageService.getFlag(),
            label: this.languageService.getLabel(),
            alt: this.languageService.getCode()
        };
    }

    /**
     * Opens the cookie settings modal
     * @public
     * @memberof SideMenuComponent
     * @param {Event} event The click event received
     */
    public openCookieSettings(event: Event): void {
        event.preventDefault();
        window[UsercentricsUI].showSecondLayer();
    }

    /**
    * Sets adobe event, on open comunity link at click event
    * @param {none}
    * @returns {nothing} Sets variables
    */
    openCommunity(event) {
        event.preventDefault();
        this.closeDrawers();
        this.adobeDataLayerService.triggerClickEvent(this.valuesService.omnitureEvents.askTheCommunityMenuEntry);
        window.open(this.linksService.community(), '_blank');
    }

    goToTermsAndPrivacy(event) {
        event.preventDefault();
        this.closeDrawers();
        window.open(this.linksService.getPrivacyPolicyLink(), '_blank');
    }

    /**
    * This opens external support at click event
    * @param {event}
    * @returns {nothing} Opens external support
    */
    openExternalSupport(event) {
        event.preventDefault();
        this.closeDrawers();
        const externalSupportLink = this.linksService.support();
        window.open(externalSupportLink, "_blank" );
    }

    openExternalSupportExtra(event, externalSupportLink) {
        event.preventDefault();
        this.closeDrawers();
        window.open(externalSupportLink, "_blank" );
    }

    /**
    * This opens change language modal from side-menu
    * @param {none}
    * @returns {nothing} Opens Modal
    */
    openChangeLanguageModal() {
        this.modalRoutelessService.open(ModalName.changeLanguageModal, { size: ModalSize.LG }, {});
    }

    /**
     * This opens info modal
     */
    openInfoModal(event) {
        event.preventDefault();
        this.closeDrawers();
        this.modalRoutelessService.open(ModalName.infoModal, { size: ModalSize.SM }, {});
    }

    /**
    * This closes drawers after interaction with the menu
    * @param {none}
    * @returns {nothing} Close drawers (side-menu, user-details, notifications)
    */
    closeDrawers() {
        this.statusDrawer.emit('close');
    }

    /**
    * This returns if a portion of the menu has displayed items
    * @param section Array containing menu items
    * @returns true or false depending on the section's items
    */
    menuSectionDisplayed(section: Array<any>): boolean {
        return section.some(el => el.show === true);
    }

    // ! Function used for underlining selected item in side menu
    // ! This will be replaced with routerLinkActive and routerLink when there is no more hybrid
    isLinkActive( link ) {
        if(this.router.url.split('/').length > 1 && this.router.url.split('/')[1] === link.split('/')[1]) {
            return true;
        } else {
            return false;
        }
    }
    //#endregion

    dipHasParkedIdentity() {
        const providedIdentity = this.privacyService.getProvidedIdentity();
        const parkedIdentity = this.privacyService.getParkedIdentityStatus();
        const deletedIdentity = this.privacyService.getIsDeletedIdentity();

        if (!providedIdentity || !providedIdentity.init_done || deletedIdentity) {
            return false;
        } else if (parkedIdentity) {
            return true;
        }
        return false;
    }

    /**
     * Shows the offline banner.
     * @returns {boolean} true If there are connectivity problems
     */
    public connectivityProblems(): boolean {
        return this.handleUnrecoverableStateService.thereAreConnectivityProblems();
    }
}
